import { useEffect, useRef, useState } from "react";
import { UploadState } from "../hooks/useReportUpload";
import "./UploadStatus.scss";

const TICK_MS = 50;
/** While the file is being sent the bar holds here; processing carries it on from this point */
const SENDING_AT = 40;
/** The bar never promises more than this until the server says it is done */
const WAITING_AT = 90;
const FINISHED = 99.5;

/**
 * One bar for the whole upload, from sending to merged. The server only reports a few coarse steps, and a small report
 * is done in about a second, so the bar creeps forward on its own (never past the point its stage allows, never behind
 * the real step) and runs on to 100% once the server finishes, instead of jumping or flashing past.
 */
const useEasedPercent = ({ phase, processProgress }: UploadState) => {
  // Mounting on a finished or resumed upload starts where it already is
  const [percent, setPercent] = useState(() =>
    phase === "completed" ? 100 : phase === "processing" ? Math.min(processProgress ?? 0, WAITING_AT) : 0
  );
  const real = useRef(0);
  real.current = phase === "processing" ? Math.min(processProgress ?? 0, WAITING_AT) : 0;

  useEffect(() => {
    if (phase !== "uploading" && phase !== "processing" && phase !== "completed") {
      setPercent(0);
      return;
    }
    const target = phase === "completed" ? 100 : phase === "processing" ? WAITING_AT : SENDING_AT;
    const rate = phase === "completed" ? 0.15 : 0.04;
    const timer = setInterval(
      () =>
        setPercent((current) => {
          const eased = Math.min(target, current + Math.max((target - current) * rate, 0.3));
          return Math.max(eased, Math.min(real.current, target), current);
        }),
      TICK_MS
    );
    return () => clearInterval(timer);
  }, [phase]);

  return percent;
};

const Dismiss = ({ onDismiss }: { onDismiss: () => void }) => (
  <button type="button" className="delete" aria-label="Dismiss" onClick={onDismiss} />
);

/** Where an uploaded report is: being sent, being processed, done or failed */
export default function UploadStatus({ state: liveState, onDismiss }: { state: UploadState; onDismiss: () => void }) {
  const state = liveState;
  const percent = useEasedPercent(state);
  const { fileName, stage, message, reportCount } = state;
  // The result waits until the bar has run to the end
  const phase = state.phase === "completed" && percent < FINISHED ? "processing" : state.phase;
  if (phase === "idle") return null;

  if (phase === "uploading" || phase === "processing") {
    const sending = phase === "uploading";
    const finishing = state.phase === "completed";
    return (
      <div className="upload-status is-processing" role="status">
        <div className="upload-status-text">
          <span>
            <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" /> {sending ? "Uploading" : finishing ? "Finishing" : "Processing"} {fileName}…
          </span>
          <strong>{Math.round(percent)}%</strong>
          <small>
            {sending
              ? "Sending the file…"
              : stage
                ? `${stage}…`
                : "The file was uploaded. Merging it into the reports usually takes under a minute."}
          </small>
        </div>
        <progress className="progress is-info is-small" value={percent} max={100} aria-label="Upload progress" />
      </div>
    );
  }

  if (phase === "completed") {
    return (
      <div className="upload-status is-completed" role="status">
        <div className="upload-status-text">
          <span>
            <i className="fa-solid fa-circle-check" aria-hidden="true" /> {fileName} has been processed
            {reportCount !== undefined ? `: ${reportCount} ${reportCount === 1 ? "report" : "reports"} loaded` : ""}.
          </span>
          <progress className="progress is-success is-small" value={100} max={100} aria-label="Upload complete" />
        </div>
        <Dismiss onDismiss={onDismiss} />
      </div>
    );
  }

  if (phase === "delayed") {
    return (
      <div className="upload-status is-delayed" role="status">
        <div className="upload-status-text">
          <span>
            <i className="fa-solid fa-hourglass-half" aria-hidden="true" /> {fileName} was uploaded but is still being processed.
          </span>
          <small>Check back shortly and reload the page to see the new data.</small>
          <progress className="progress is-warning is-small" max={100} aria-label="Processing progress" />
        </div>
        <Dismiss onDismiss={onDismiss} />
      </div>
    );
  }

  return (
    <div className="upload-status is-failed" role="alert">
      <div className="upload-status-text">
        <span>
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> {fileName} could not be uploaded or processed.
        </span>
        <small>{message}</small>
      </div>
      <Dismiss onDismiss={onDismiss} />
    </div>
  );
}
